import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { formValueSelector, Field, Fields, FormSection } from "redux-form";
import { get } from "lodash";
import { Map, fromJS } from "immutable";
import classNames from "classnames";

import api from "APP_ROOT/api";
import {
  form,
  Form as AppForm,
  AutoCompleteField,
  TextArea,
  Select
} from "MODULES/shared/components/Forms";
import IncomingQuoteTypeSelect from "MODULES/IncomingQuotes/IncomingQuoteTypeSelect";
import { LineItemsTable as IncomingQuoteTable } from "MODULES/parts/components/Detail/IncomingQuotes/LineItemsTable";
import { Table as SalesOrdersTable } from "MODULES/parts/components/Detail/SalesOrders/Table";
import { Table as PurchaseOrdersTable } from "MODULES/parts/components/Detail/PurchaseOrders/Table";
import {
  AWAITING_REPLY,
  INCIDENT_RELATED,
  INCOMING_LINE_ITEMS,
  NO_QUOTE
} from "MODULES/IncomingQuotes/constants";
import Total from "MODULES/IncomingQuotes/Total";
import SelectInventoryTable from "MODULES/OutgoingQuotes/shared/SelectInventoryTable";
import ExistingQuoteWarning from "MODULES/parts/components/Detail/IncomingQuotes/Create/ExistingQuoteWarning";
import { isLessThan36HoursAgo } from "LIB/time";
import {
  CAAC,
  DO_NOT_LIST,
  EASA,
  FAA,
  LIST,
  OTHER,
  OTHER_TAG_TYPE,
  PART_ID,
  TAGGED_BY,
  TCCA,
  UK_CAA
} from "MODULES/inventories/constants";
import { COST, ID, QUANTITY } from "APP_ROOT/constants";
import { EXCHANGE_FEE, QUOTE_TYPE } from "MODULES/OutgoingQuotes/constants";
import FormRow from "MODULES/shared/components/Forms/FormRow";
import { Col } from "react-bootstrap";
import { COMPANY } from "MODULES/companies/constants";
import TagTypes from "MODULES/inventories/components/EditInventory/TagTypes";
import { EXTERNAL_NOTE } from "MODULES/PurchaseOrders/constants";
import { PART, SELLABLE_QUANTITIES } from "MODULES/parts/constants";
import { properCase } from "LIB/formatters/text";
import PartDetails from "./PartDetails";
import SellableQty from "../../parts/components/shared/SellableQty";
import validate from "./validate";

const FORM_NAME = "incomingQuoteDetailAddPart";

export class Form extends Component {
  static propTypes = {
    quantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    cost: PropTypes.string,
    quoteType: PropTypes.string,
    companyId: PropTypes.number,
    quoteTypes: PropTypes.instanceOf(Object),
    selectedLineItem: PropTypes.shape({
      part: PropTypes.shape({}),
      id: PropTypes.number
    }),
    searchParts: PropTypes.func.isRequired,
    setPart: PropTypes.func
  };

  state = {
    selectedPart: get(this.props.selectedLineItem, PART),
    recentIQs: [],
    recentSOs: [],
    recentPOs: [],
    alternateOptions: [],
    recentCompanyQuotes: []
  };
  componentWillMount() {
    if (this.props.selectedLineItem && this.props.selectedLineItem.part) {
      const part = this.props.selectedLineItem.part;
      part.base_part_number = part.part_number;
      this.handlePartSelected(part);
    }
  }

  componentWillReceiveProps(nextProps) {
    if (
      nextProps.selectedLineItem &&
      nextProps.selectedLineItem !== this.props.selectedLineItem
    ) {
      this.setState({ selectedPart: nextProps.selectedLineItem.part });
    }
  }

  searchParts = filter =>
    api.parts
      .searchPartsAndAlternates({
        part_number: filter,
        search_type: "search",
        sold_recently_status: true
      })
      .then(resp => resp.data.results);

  fetchAlternateOptions = (partId, defaultAlt) => {
    if (partId) {
      return api.parts.alternates.search(partId).then(resp => {
        const vals = resp.data.results;
        const opts = vals.map(val => ({
          text: val.name,
          value: val.id
        }));

        return this.setState({ alternateOptions: opts }, () => {
          if (defaultAlt) {
            this.props.change("rfq_as_alt_id", defaultAlt.id);
          }
        });
      });
    }

    return null;
  };

  handlePartSelected = partOrAlt => {
    const part = {
      part_number: partOrAlt.base_part_number,
      id: partOrAlt.id
    };
    this.setState({ selectedPart: part });

    if (this.props.setPart) {
      this.props.setPart(part);
    }

    const alt = partOrAlt.alternate
      ? { id: partOrAlt.alternate_id, part_number: partOrAlt.part_number }
      : null;

    this.fetchAlternateOptions(part.id, alt);

    api.parts.incomingQuoteLineItems
      .search(part.id, { limit: 5 })
      .then(({ data: { results: recentIQs } }) => {
        const filteredIQs = recentIQs.filter(
          quote =>
            !this.props.selectedLineItem ||
            this.props.selectedLineItem.id !== quote.id
        );
        this.setState({ recentIQs: filteredIQs });
      });

    api.parts.purchaseOrderAndLots
      .search(part.id, { limit: 3 })
      .then(({ data: { results: recentPOs } }) => {
        this.setState({ recentPOs });
      });

    api.parts.salesOrderLineItems
      .search(part.id, { limit: 3 })
      .then(({ data: { results: recentSOs } }) => {
        this.setState({ recentSOs });
      });

    this.fetchRecentCompanyQuotes(part.id);
  };

  fetchRecentCompanyQuotes = partId => {
    const { companyId, selectedLineItem } = this.props;

    if (partId && companyId) {
      api.parts.incomingQuoteLineItems
        .search(partId, { limit: 5, company_id: companyId })
        .then(res => {
          const filteredQuotes = res.data.results.filter(
            quote =>
              isLessThan36HoursAgo(quote.created_at) &&
              (!selectedLineItem || selectedLineItem.id !== quote.id)
          );

          this.setState({ recentCompanyQuotes: filteredQuotes });
        });
    }
  };

  formatPartSearch = part => {
    if (part.alternate) {
      return `${part.part_number} - ${part.base_part_number}`;
    }

    return part.part_number;
  };

  render() {
    const {
      quantity,
      cost,
      quoteType,
      quoteTypes = INCOMING_LINE_ITEMS.quoteType
    } = this.props;
    const {
      selectedPart,
      recentPOs,
      recentIQs,
      recentSOs,
      alternateOptions,
      recentCompanyQuotes
    } = this.state;

    const notesColumnClassName = classNames({
      "col-md-12": quoteType !== AWAITING_REPLY,
      "col-md-6": quoteType === AWAITING_REPLY
    });

    const types = Object.assign({}, quoteTypes);

    if (quoteType !== INCIDENT_RELATED) {
      delete types.incident_related;
    }

    return (
      <AppForm {...this.props}>
        <AppForm.Body>
          <div className="nav nav-tabs">
            <h4>Quote Type & Part Number</h4>
          </div>
          {recentCompanyQuotes.length > 0 && (
            <ExistingQuoteWarning quote={fromJS(recentCompanyQuotes[0])} />
          )}
          <FormRow>
            <Col md={8}>
              <div
                className="row row-col-gutters-half"
                style={{ marginTop: "26px" }}
              >
                <Col md={4}>
                  <Fields
                    names={[COST, QUANTITY, EXCHANGE_FEE]}
                    component={IncomingQuoteTypeSelect}
                    quoteTypes={types}
                  />
                </Col>
                <Col md={4}>
                  <div className="form-group">
                    <Field
                      autoFocus
                      name={PART_ID}
                      label="Part #"
                      placeholder="Type a part's name of ID number..."
                      component={AutoCompleteField}
                      search={this.searchParts}
                      formatItem={this.formatPartSearch}
                      selectedItem={selectedPart}
                      onItemSelected={i => i && this.handlePartSelected(i)}
                      required
                    />
                  </div>
                </Col>
                <Col md={4}>
                  <div className="form-group">
                    <Field
                      name="rfq_as_alt_id"
                      label="RFQ as Alt"
                      component={Select}
                      options={alternateOptions}
                    />
                  </div>
                </Col>
              </div>
              <hr className="hr-sm" />
              <SellableQty
                quantities={Map(
                  fromJS(get(selectedPart, SELLABLE_QUANTITIES, {}))
                )}
              />
              <h4 className="margin-bottom-30 line-height-2">
                Next Available Inventory
              </h4>
              <div className="form-group">
                <FormSection name="ingoing_quote_line_item">
                  <SelectInventoryTable
                    filter={{
                      limit: "5",
                      sellable_status: [LIST, DO_NOT_LIST].join(",")
                    }}
                    part={selectedPart}
                    choosable={false}
                    modal={false}
                    displayQuantity={false}
                  />
                </FormSection>
              </div>
              {quoteType !== NO_QUOTE && [
                <div>
                  <hr className="hr-sm" />
                  <h4>Part Details</h4>
                  <PartDetails
                    quoteType={quoteType}
                    taggedBy={get(this.props.selectedLineItem, TAGGED_BY)}
                  />
                </div>,
                <FormRow>
                  <Col md={12}>
                    <Fields
                      component={TagTypes}
                      names={[
                        FAA,
                        EASA,
                        UK_CAA,
                        CAAC,
                        TCCA,
                        OTHER,
                        OTHER_TAG_TYPE
                      ]}
                      formName={FORM_NAME}
                    />
                  </Col>
                </FormRow>
              ]}
              <hr className="hr-xs hr-dashed" />
              <h4>Most Recent Incoming Quotes</h4>
              <IncomingQuoteTable results={recentIQs} disableRowNav inForm />
              <h4>Most Recent Sales Orders</h4>
              <SalesOrdersTable results={recentSOs} disableRowNav />
              <h4>Most Recent Purchase Orders</h4>
              <PurchaseOrdersTable results={recentPOs} disableRowNav />

              <FormRow>
                <div className={notesColumnClassName}>
                  <div className="form-group">
                    <Field
                      name="notes"
                      component={TextArea}
                      label="Note (Applied to the line item)"
                    />
                  </div>
                </div>
                {quoteType === AWAITING_REPLY && (
                  <div className={notesColumnClassName}>
                    <div className="form-group">
                      <Field
                        name={EXTERNAL_NOTE}
                        component={TextArea}
                        label={properCase(EXTERNAL_NOTE)}
                      />
                    </div>
                  </div>
                )}
              </FormRow>
            </Col>
            <Col md={4}>
              <Total part={selectedPart} quantity={quantity} cost={cost} />
            </Col>
          </FormRow>
        </AppForm.Body>
      </AppForm>
    );
  }
}

const selector = formValueSelector(FORM_NAME);

export default connect(state => ({
  quantity: selector(state, QUANTITY),
  cost: selector(state, COST),
  quoteType: selector(state, QUOTE_TYPE),
  companyId: state.incomingQuoteDetail.getIn(["incomingQuote", COMPANY, ID])
}))(
  form({
    form: FORM_NAME,
    validate
  })(Form)
);



// WEBPACK FOOTER //
// ./app/modules/IncomingQuotes/Parts/Form.jsx